import { For, Show, createEffect, createSignal } from "solid-js";

import { Button } from "@/components/ui/Button";
import { t } from "@/lib/i18n";

import { resetTheme, showThemeNotice, themeNotice, themePalette, updateTheme } from "./apply";
import { type AppPalette, PRESETS, isDefaultPalette, normalizeHex, palettesEqual } from "./palette";

const FIELDS: Array<{ key: keyof AppPalette; labelKey: string }> = [
  { key: "page", labelKey: "appearance.page" },
  { key: "surface", labelKey: "appearance.surface" },
  { key: "header", labelKey: "appearance.header" },
  { key: "sidebar", labelKey: "appearance.sidebar" },
  { key: "sidebarText", labelKey: "appearance.sidebar_text" },
  { key: "sidebarActive", labelKey: "appearance.sidebar_active" },
  { key: "accent", labelKey: "appearance.accent" },
];

function ColorControl(props: { label: string; value: string; onCommit: (hex: string) => void }) {
  const [draft, setDraft] = createSignal(props.value);
  createEffect(() => setDraft(props.value));

  return (
    <div class="flex items-center gap-3">
      <input
        type="color"
        value={props.value}
        aria-label={props.label}
        class="h-10 w-12 shrink-0 cursor-pointer rounded-[10px] border border-[var(--border-default)] bg-transparent"
        onInput={(event) => {
          const hex = normalizeHex(event.currentTarget.value);
          if (hex) props.onCommit(hex);
        }}
      />
      <label class="min-w-0 flex-1">
        <span class="mb-1.5 block text-[13px] font-semibold text-[var(--fg-default)]">
          {props.label}
        </span>
        <input
          value={draft()}
          spellcheck={false}
          autocapitalize="off"
          autocomplete="off"
          class="h-10 w-full rounded-[10px] border border-[var(--border-strong)] bg-[var(--bg-surface)] px-3 font-mono text-sm text-[var(--fg-strong)]"
          onInput={(event) => {
            const next = event.currentTarget.value;
            setDraft(next);
            const hex = normalizeHex(next);
            if (hex) props.onCommit(hex);
          }}
          onBlur={() => {
            const hex = normalizeHex(draft());
            if (hex) {
              props.onCommit(hex);
              return;
            }
            if (draft().trim()) showThemeNotice("appearance.invalid");
            setDraft(props.value);
          }}
        />
      </label>
    </div>
  );
}

export function AppearancePanel(props: { heading?: boolean }) {
  const showHeading = () => props.heading !== false;
  const current = themePalette;

  return (
    <section
      id={props.heading === false ? undefined : "apparence"}
      data-testid="appearance-panel"
      class="rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)]"
    >
      <Show when={showHeading()}>
        <header class="border-b border-[var(--border-default)] px-6 py-4">
          <h3 class="font-display text-base font-semibold text-[var(--fg-strong)]">
            {t("appearance.title")}
          </h3>
          <p class="mt-1 text-sm text-[var(--fg-muted)]">{t("appearance.subtitle")}</p>
        </header>
      </Show>
      <div class="space-y-5 p-6">
        <Show when={!showHeading()}>
          <p class="text-sm text-[var(--fg-muted)]">{t("appearance.subtitle")}</p>
        </Show>

        <div
          class="overflow-hidden rounded-xl border border-[var(--border-default)]"
          aria-hidden="true"
        >
          <div class="flex h-28">
            <div
              class="flex w-28 flex-col justify-center gap-1.5 px-2"
              style={{ background: "var(--sidebar-bg)", color: "var(--sidebar-fg)" }}
            >
              <span
                class="truncate rounded-md px-2 py-1 text-[11px] font-semibold"
                style={{
                  background: "var(--sidebar-active)",
                  color: "var(--sidebar-active-fg)",
                }}
              >
                {t("appearance.sidebar_active")}
              </span>
              <span class="truncate px-2 text-[11px]" style={{ color: "var(--sidebar-fg-muted)" }}>
                {t("appearance.sidebar")}
              </span>
            </div>
            <div class="flex min-w-0 flex-1 flex-col" style={{ background: "var(--bg-base)" }}>
              <div
                class="flex h-8 items-center border-b px-3 text-[11px] font-semibold"
                style={{
                  background: "var(--bg-header)",
                  color: "var(--header-fg)",
                  "border-color": "var(--header-border)",
                }}
              >
                {t("appearance.header")}
              </div>
              <div class="flex flex-1 items-center px-3">
                <span
                  class="rounded-[10px] px-3 py-1.5 text-[11px] font-semibold"
                  style={{ background: "var(--accent-base)", color: "var(--accent-fg)" }}
                >
                  {t("appearance.accent")}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p class="mb-2 text-[13px] font-semibold text-[var(--fg-default)]">
            {t("appearance.presets")}
          </p>
          <div class="flex flex-wrap gap-2">
            <For each={PRESETS}>
              {(preset) => (
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-[10px] border px-2.5 py-1.5 text-[13px] font-semibold text-[var(--fg-strong)] transition"
                  classList={{
                    "border-[var(--accent-alt)] bg-[var(--accent-soft)]": palettesEqual(
                      current(),
                      preset.palette
                    ),
                    "border-[var(--border-default)] bg-[var(--bg-surface)] hover:border-[var(--border-strong)]":
                      !palettesEqual(current(), preset.palette),
                  }}
                  aria-pressed={palettesEqual(current(), preset.palette)}
                  onClick={() => updateTheme(preset.palette)}
                >
                  <span class="flex overflow-hidden rounded-full">
                    <span class="h-3 w-3" style={{ background: preset.palette.page }} />
                    <span class="h-3 w-3" style={{ background: preset.palette.sidebar }} />
                    <span class="h-3 w-3" style={{ background: preset.palette.accent }} />
                  </span>
                  {t(preset.labelKey)}
                </button>
              )}
            </For>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <For each={FIELDS}>
            {(field) => (
              <ColorControl
                label={t(field.labelKey)}
                value={current()[field.key]}
                onCommit={(hex) => updateTheme({ [field.key]: hex })}
              />
            )}
          </For>
        </div>

        <Show when={themeNotice()}>
          {(issue) => (
            <p role="alert" class="text-sm text-[var(--danger)]">
              {t(issue())}
            </p>
          )}
        </Show>

        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="max-w-md text-xs text-[var(--fg-muted)]">{t("appearance.stored")}</p>
          <Button
            variant="secondary"
            onClick={() => resetTheme()}
            disabled={isDefaultPalette(current())}
          >
            {t("appearance.reset")}
          </Button>
        </div>
      </div>
    </section>
  );
}
